import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, SmilePlus } from "lucide-react";
import { QUICK_REACTIONS, REACTIONS, showReaction } from "@/lib/inbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/cn";

/**
 * "React" beside a message, the way Telegram offers it: a quick row, and behind
 * the arrow the whole of its standard set. Picking the one already on the
 * message takes it off, another one replaces it.
 */
export function ReactionPicker({
  current,
  onPick,
  className,
}: {
  current: string | null;
  /** The emoji to put, or null to take the reaction off. */
  onPick: (emoji: string | null) => void;
  className?: string;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [all, setAll] = useState(false);
  const panel = useRef<HTMLDivElement>(null);
  // Each time it opens with the quick row again.
  const close = () => {
    setOpen(false);
    setAll(false);
  };
  useEffect(() => {
    // The arrow that was pressed is no longer in the panel: without this the
    // keyboard's focus is left on the page behind it.
    if (all) panel.current?.querySelector("button")?.focus();
  }, [all]);
  const button =
    "flex h-8 w-8 items-center justify-center rounded-md text-lg leading-none transition-colors hover:bg-bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent";

  return (
    <Popover open={open} onOpenChange={(next) => (next ? setOpen(true) : close())}>
      <PopoverTrigger
        type="button"
        aria-label={t("inbox.reaction.action")}
        title={t("inbox.reaction.action") ?? ""}
        className={className}
      >
        <SmilePlus size={15} strokeWidth={2} aria-hidden="true" />
      </PopoverTrigger>
      <PopoverContent
        ref={panel}
        align="center"
        className={cn(
          "w-auto gap-0.5 p-1",
          all ? "grid max-h-56 grid-cols-8 overflow-y-auto overflow-x-hidden" : "flex",
        )}
      >
        {(all ? REACTIONS : QUICK_REACTIONS).map((emoji) => (
          <button
            key={emoji}
            type="button"
            aria-pressed={emoji === current}
            onClick={() => {
              onPick(emoji === current ? null : emoji);
              close();
            }}
            className={cn(button, emoji === current && "bg-accent/20")}
          >
            {showReaction(emoji)}
          </button>
        ))}
        {!all && (
          <button
            type="button"
            aria-label={t("inbox.reaction.all")}
            title={t("inbox.reaction.all")}
            onClick={() => setAll(true)}
            className={cn(button, "text-fg-muted")}
          >
            <ChevronDown size={16} strokeWidth={2} aria-hidden="true" />
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
}
ReactionPicker.displayName = "ReactionPicker";
